import { Box, Divider, Typography, Link as MuiLink } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DOMPurify from 'dompurify';
import type { Course } from '../services/courseService';
import { getDeliveryMethod, findDisclosureSectionText } from '../utils/nasbaDisclosure';

// NASBA-required disclosures for CPE "program announcements" (course detail pages).
// NASBA expects Prerequisites and Advance Preparation to be stated explicitly even
// when there are none, so those rows fall back to "None" instead of being omitted.

const labelSx = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  mb: 0.5,
  px: 2,
} as const;

function DisclosureRow({ label, html, text }: { label: string; html?: string; text?: string }) {
  return (
    <>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ mb: 2 }}>
        <Typography sx={labelSx}>{label}</Typography>
        {html ? (
          <Box
            sx={{ fontSize: '14px', lineHeight: 1.43, color: 'text.primary', px: 2, maxWidth: '72ch' }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }}
          />
        ) : (
          <Typography variant="body2" sx={{ fontSize: '14px', px: 2 }}>
            {text}
          </Typography>
        )}
      </Box>
    </>
  );
}

interface NasbaDisclosuresProps {
  course: Course;
}

export default function NasbaDisclosures({ course }: NasbaDisclosuresProps) {
  const learningObjectives = findDisclosureSectionText(course, 'learningObjectives');
  const prerequisites = findDisclosureSectionText(course, 'prerequisites');
  const advancePreparation = findDisclosureSectionText(course, 'advancedPreparation');

  return (
    <>
      {learningObjectives && <DisclosureRow label="Learning Objectives" html={learningObjectives} />}
      {prerequisites ? (
        <DisclosureRow label="Prerequisites" html={prerequisites} />
      ) : (
        <DisclosureRow label="Prerequisites" text="None" />
      )}
      {advancePreparation ? (
        <DisclosureRow label="Advance Preparation" html={advancePreparation} />
      ) : (
        <DisclosureRow label="Advance Preparation" text="None" />
      )}
      <DisclosureRow label="Delivery Method" text={getDeliveryMethod(course)} />
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" sx={{ display: 'block', px: 2, color: 'text.secondary', lineHeight: 1.6 }}>
        For registration and attendance requirements, see our{' '}
        <MuiLink component={RouterLink} to="/app/faq">
          FAQs
        </MuiLink>
        . For refund, cancellation, and complaint resolution policies, see our{' '}
        <MuiLink component={RouterLink} to="/app/refund-policy">
          refund policy
        </MuiLink>{' '}
        and FAQs.
      </Typography>
    </>
  );
}
